import { request } from './utils';
import { formatDate, dealerServerSidePaginationSearchParams } from './../utils/formatters';
import { downloadReport } from './../utils/downloadHelper';

export default class DealerService {
    static async getAll() {
        const response = await request('/api/dealers');
        return response.data;
    }

    static async getAllPaginated(page, perPage, searchText, filterList) {
        const response = await request(`/api/dealers${dealerServerSidePaginationSearchParams(page, perPage, searchText, filterList)}`);
        return response.data;
    }

    // TODO: `/dealers/all/extra` had no DPP route and has no P4 equivalent.
    // Confirm whether this is still used; if so, P4 needs an endpoint.
    static async getAllWithAdditionalMetrics() {
        const response = await request('/api/dealers');
        return response.data;
    }

    static async get(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}`);
        return response.data;
    }

    static async getDelinquencies(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/active_delinquencies`);
        // P4 returns { delinquencies: [...] } — DPP unwrapped it.
        return response.data?.delinquencies;
    }

    static async getActiveLoans(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/active_loans`);
        return response.data?.activeLoans;
    }

    static async getOutstandingFunds(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/outstanding_funds`);
        return response.data?.outstandingFunds;
    }

    static async getDealerPeopleFundedMetrics(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/people_funded_metrics`);
        return response.data?.peopleFundedMetrics;
    }

    static async getDealerFundingYTD(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/total_funded_year_to_date`);
        return response.data[0];
    }

    static async getDealerPromoCount(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/promo_count`);
        return response.data;
    }

    static async getAllUnique() {
        const response = await request('/api/dealers/unique_business_names');
        return response.data;
    }

    static async getAllOrgs() {
        const response = await request('/api/dealers/organizations');
        return response.data;
    }

    static async newDealerComment(dealerNumber, comment, category) {
        const response = await request(`/api/dealers/${dealerNumber}/dealer_comments`, {
            method: 'POST',
            data: JSON.stringify({ comment, category })
        });

        return response;
    }

    static async deleteDealerComment(id) {
        const response = await request(`/api/dealer_comments/${id}/inactivate`, {
            method: 'PATCH'
        });

        return response;
    }

    static async deleteTestTransaction(id) {
        const response = await request(`/api/test_transactions/${id}`, {
            method: 'DELETE'
        });
        return response;
    }

    static async getDealerCommentsForDealer(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/dealer_comments`);
        // DPP wrapped P4's array as { comments: [...] }.
        return { comments: response.data };
    }

    static async getDealerPopupsForDealer(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/dealer_comments/popup_comments`);
        return { comments: response.data };
    }

    static async updateDealer(dealer) {
        const response = await request(`/api/dealers/${dealer.dealerNumber}`, {
            method: 'PATCH',
            data: JSON.stringify(dealer)
        });

        return response;
    }

    static async updateDealerManualFlag(newValue, dealerNumber, flagReason) {
        // DPP mapped { newValue, flagReason } -> { isManualFlagged, manualFlaggedReason }.
        const response = await request(`/api/dealers/${dealerNumber}`, {
            method: 'PATCH',
            data: JSON.stringify({ isManualFlagged: newValue, manualFlaggedReason: flagReason })
        });

        return response;
    }

    static async updateDealerHasFincenIDFlag(newValue, dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}`, {
            method: 'PATCH',
            data: JSON.stringify({ hasFincenId: newValue })
        });

        return response;
    }

    static async getDealerHistory(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/history`);

        return response.data;
    }

    static async refreshDealerMetric() {
        const response = await request('/api/dealer_metrics/refresh', {
            method: 'POST'
        });
        return response;
    }

    static async getAllTestTransactions() {
        const response = await request('/api/test_transactions/all');
        return response.data;
    }

    static async resetTestTransactionCurrentDate() {
        const formattedDate = formatDate(new Date());
        const response = await request('/api/test_transactions/reset', {
            method: 'PUT',
            data: JSON.stringify({
                forDate: formattedDate,
                transactionStatus: 'ready to send',
                sendDate: null
            })
        });

        return response.status;
    }

    static async getDealerTestTransactions(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/test_transactions`);
        return response.data;
    }

    static async getBpsSchedules(dealerNumber) {
        const response = await request(`/api/dealers/${dealerNumber}/bps_schedules`);
        return response.data;
    }

    static async newBpsSchedule(dealerNumber, { days, basisPoints }) {
        const response = await request(`/api/dealers/${dealerNumber}/bps_schedules`, {
            method: 'POST',
            data: JSON.stringify({ days, basisPoints })
        });
        return response;
    }

    static async updateBpsSchedule(dealerNumber, id, { days, basisPoints }) {
        const response = await request(`/api/dealers/${dealerNumber}/bps_schedules/${id}`, {
            method: 'PATCH',
            data: JSON.stringify({ days, basisPoints })
        });
        return response;
    }

    static async deleteBpsSchedule(dealerNumber, id) {
        const response = await request(`/api/dealers/${dealerNumber}/bps_schedules/${id}`, {
            method: 'DELETE'
        });
        return response;
    }

    // NOTE: DPP previously enforced TEST_TRANSACTION_LOWER/UPPER_LIMIT bounds
    // before forwarding. That validation belongs in P4 — it now owns it.
    static async newTestTransaction(dealerNumber, amount) {
        const response = await request('/api/test_transactions', {
            method: 'POST',
            data: JSON.stringify({ dealerNumber, amountCents: amount })
        });
        return response;
    }

    static async updateTestTransaction(trxnId, status) {
        const response = await request(`/api/test_transactions/${trxnId}`, {
            method: 'PUT',
            data: JSON.stringify({ transactionStatus: status })
        });
        return response;
    }

    static async updateDealerTestTransaction(dealerNumber, status) {
        const response = await request(`/api/dealers/${dealerNumber}`, {
            method: 'PUT',
            data: JSON.stringify({ testTransStatus: status })
        });
        return response;
    }

    static async verifyBulkTestTransactions(trxnIds) {
        const response = await request('/api/test_transactions/batch_status_update', {
            method: 'PUT',
            data: JSON.stringify({ ids: trxnIds, status: 'confirmed' })
        });
        return response;
    }

    static async verifyBulkDealers(dealerIds) {
        const response = await request('/api/dealers/statuses', {
            method: 'PATCH',
            data: JSON.stringify({ ids: dealerIds, testTransStatus: 'confirmed' })
        });
        return response;
    }

    static async downloadNachaTrxnFile() {
        await downloadReport('/api/test_transactions/wsfs_nacha', 'WSFS Test Trxns Nacha');
    }
}
